import React, { useEffect, useState } from "react";

import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
} from "@mui/material";
import { observer } from "mobx-react-lite";
import { makeStyles } from "tss-react/mui";

import { useSchedule } from "../SchedulerProvider";
import Rule from "../store/Rule";

const WEEKDAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

type Props = {
  open: boolean;
  onClose: () => void;
  // The rule whose intervals are being copied, and the label to show for it.
  sourceRule: Rule | null;
  sourceLabel: string;
};

// Copy one day's availability onto other weekdays' recurring hours in bulk —
// the "apply these hours to multiple days" flow.
const CopyHoursDialog: React.FC<Props> = observer(
  ({ open, onClose, sourceRule, sourceLabel }) => {
    const { classes } = useStyles();
    const schedule = useSchedule();
    const [selected, setSelected] = useState<Set<string>>(new Set());

    useEffect(() => {
      if (open) setSelected(new Set());
    }, [open]);

    const toggle = (wday: string) => {
      setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(wday)) next.delete(wday);
        else next.add(wday);
        return next;
      });
    };

    const handleApply = () => {
      if (sourceRule) {
        const recipients = schedule.wdayRules.filter(
          (rule) => rule.wday && selected.has(rule.wday)
        );
        if (recipients.length > 0) {
          schedule.pushHistory();
          schedule.copyIntervals(sourceRule, recipients);
        }
      }
      onClose();
    };

    return (
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle>Copy hours to other days</DialogTitle>
        <DialogContent>
          <Typography className={classes.subtitle}>
            Copy {sourceLabel}&apos;s hours onto these weekdays:
          </Typography>
          {WEEKDAYS.map((wday) => (
            <FormControlLabel
              key={wday}
              control={
                <Checkbox
                  checked={selected.has(wday)}
                  onChange={() => toggle(wday)}
                  size="small"
                />
              }
              label={wday}
            />
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            disabled={selected.size === 0 || !sourceRule}
            onClick={handleApply}
          >
            Copy
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
);

const useStyles = makeStyles()(() => ({
  subtitle: {
    fontSize: 13,
    color: "#4C515A",
    marginBottom: 8,
  },
}));

export default CopyHoursDialog;
